import { useEffect, useState } from "react";
import { X, Send } from "lucide-react";
import ModalOverlay from "../UI/ModalOverlay/ModalOverlay.tsx";
import styles from "./ApplyInternalOfferModal.module.scss";
import JobApplicationCvField from "../WorkBag/JobApplicationCvField/JobApplicationCvField.tsx";
import { getCoverLetterValidationError } from "../../utils/jobboard/coverLetter.ts";
import { resolveApplicationCvForPostulation } from "../../utils/userCv.ts";

export interface ApplyInternalOfferSubmitData {
  cv: File | "";
  coverLetter: string;
}

interface ApplyInternalOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ApplyInternalOfferSubmitData) => Promise<void>;
  isSubmitting: boolean;
  alreadyApplied?: boolean;
  appliedAt?: string | null;
  profileCvUrl?: string | null;
  profileCvUpdatedAt?: string | null;
  isAuthenticated?: boolean;
}

const ApplyInternalOfferModal: React.FC<ApplyInternalOfferModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  alreadyApplied = false,
  appliedAt = null,
  profileCvUrl = null,
  profileCvUpdatedAt = null,
  isAuthenticated = false,
}) => {
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvFileName, setCvFileName] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [formError, setFormError] = useState("");
  const [useProfileCv, setUseProfileCv] = useState(Boolean(profileCvUrl));

  useEffect(() => {
    if (isOpen) {
      setUseProfileCv(Boolean(profileCvUrl));
    }
  }, [isOpen, profileCvUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (alreadyApplied || isSubmitting) return;
    const coverLetterError = getCoverLetterValidationError(coverLetter, false);
    if (coverLetterError) {
      setFormError(coverLetterError);
      return;
    }
    setFormError("");
    const cv = resolveApplicationCvForPostulation({
      cvFile,
      useProfileCv,
      profileCvUrl,
    });
    await onSubmit({
      cv,
      coverLetter: coverLetter.trim(),
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCvFile(null);
      setCvFileName("");
      setCoverLetter("");
      setFormError("");
      setUseProfileCv(Boolean(profileCvUrl));
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Completar postulación</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Cerrar"
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </header>
        <p className={styles.subtitle}>
          Podés adjuntar tu CV y una carta de presentación para reforzar tu postulación. Ambos son opcionales.
        </p>
        {alreadyApplied && (
          <div className={styles.statusBanner}>
            Ya registramos tu postulación para esta oferta{appliedAt ? ` (${new Date(appliedAt).toLocaleDateString("es-AR")})` : ""}.
          </div>
        )}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="apply-internal-cv" className={styles.label}>
              CV (opcional)
            </label>
            <JobApplicationCvField
              inputId="apply-internal-cv"
              profileCvUrl={profileCvUrl}
              profileCvUpdatedAt={profileCvUpdatedAt}
              cvFile={cvFile}
              cvFileName={cvFileName}
              useProfileCv={useProfileCv}
              onCvFileChange={(file, name) => {
                setCvFile(file);
                setCvFileName(name);
              }}
              onUseProfileCvChange={setUseProfileCv}
              disabled={isSubmitting}
              showProfileSaveHint={isAuthenticated && !profileCvUrl}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="coverLetter" className={styles.label}>
              Carta de presentación (opcional, mín. 50 caracteres si la completás)
            </label>
            <textarea
              id="coverLetter"
              name="coverLetter"
              className={styles.textarea}
              placeholder="Contá brevemente por qué te interesa esta oportunidad..."
              value={coverLetter}
              onChange={(e) => {
                setCoverLetter(e.target.value);
                setFormError("");
              }}
              rows={5}
              disabled={isSubmitting}
            />
          </div>
          {formError ? <p className={styles.formError}>{formError}</p> : null}
          <footer className={styles.footer}>
            <button type="button" className={styles.secondaryButton} onClick={handleClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className={styles.primaryButton} disabled={isSubmitting || alreadyApplied}>
              {alreadyApplied ? "Ya postulaste" : isSubmitting ? "Enviando..." : "Enviar postulación"}
              <Send size={16} />
            </button>
          </footer>
        </form>
      </div>
    </ModalOverlay>
  );
};

export default ApplyInternalOfferModal;
